import { AppState } from "../../store/root.reducer";
import { ApiClient } from "../../utils/apiClient";
import React from "react";
import { Modal, ToastContainer, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useSelector } from "react-redux";
import Select, { MultiValue, StylesConfig } from "react-select";
import { toast } from "react-toastify";
import { Images, Sun, People, Lock, Pencil, Trash, Plus, VolumeMuteFill, VolumeUpFill, EyeSlash, Search, ChevronLeft, ChevronRight, XLg } from "react-bootstrap-icons";
import { getMoodAudienceType, getVisibleMoodEntries } from "../../utils/moodVisibility";
import { ConfirmModal } from "./confirm-modal";
import { ExternalSound, Sounds } from "../../types/soundsData";
import { Artwork } from "../../types/artworksData";
import listStyles from "./modal.module.css";
import "./new.artwork.css";
import type { GalleryLabel } from "@/models/gallery-label.model";
import type { TagOption } from "./new.artwork";
import { getProxiedSoundUrl } from "../../utils/imageProxy";

const galleryLabelTagStylesBase: StylesConfig<TagOption, true> = {
    control: (styles) => ({ ...styles, backgroundColor: "white" }),
    option: (styles, { isDisabled }) => {
        const color = "#000000";
        return {
            ...styles,
            color,
            cursor: isDisabled ? "not-allowed" : "default",
            ":active": {
                ...styles[":active"],
                backgroundColor: color,
            },
        };
    },
};

export function extractGalleryLabelIds(
    gallery: { labelIds?: string[]; labels?: Array<string | { _id?: string }> } | null | undefined
): string[] {
    if (!gallery) return [];
    if (Array.isArray(gallery.labelIds)) {
        return gallery.labelIds.filter((id): id is string => Boolean(id));
    }
    if (Array.isArray(gallery.labels)) {
        return gallery.labels
            .map((l) => (typeof l === "string" ? l : l?._id))
            .filter((id): id is string => Boolean(id));
    }
    return [];
}

type CollaboratorRow = {
    userId: string;
    email: string;
    role: "owner" | "editor" | "contributor";
};

type PseudoMomIntroSoundSettings = {
    soundId: string;
    url: string;
    soundVolume: number;
} | null;

type Props = {
    personal: boolean;
    /** If true, personal-only management sections can render in an equivalent admin context */
    canUseManagementSections?: boolean;
    /** If true, user can add/edit/remove collaborators (owner only) */
    canManageCollaborators?: boolean;
    /** If true, user can manage gallery settings including sounds (owner or editor) */
    canManageGallery?: boolean;
    galleryId: string;
    galleryOwnerId?: string;
    collaborators?: Array<{ userId: string; email: string; role: "editor" | "contributor" }>;
    onCollaboratorsUpdated?: () => void;
    includeUnpublishedMoods: boolean;
    moods: any[]
    currentMood: number;
    onChangeMood: (newMood: number) => Promise<void>;
    show: boolean;
    published: boolean;
    onChangePublished: (status: boolean) => void;
    onHide: () => void;
    onPlaying: (p: boolean) => void;
    onMute: (mute: boolean) => Promise<void>;
    currentTitle: string;
    onChangeTitle: (newTitle: string) => void;
    /** Currently selected sound ID for the gallery */
    currentSoundId?: string;
    /** Whether background sound is currently muted */
    currentMuted?: boolean;
    /** Whether background sound is currently loading */
    isBackgroundSoundLoading: boolean;
    /** Callback when sound is changed; receives new sound data to update appData */
    onSoundChanged: (sound: Sounds) => Promise<void>;
    /** Pause 3D background ambient while previewing Europeana (or other) sounds */
    onPauseBackgroundSound?: () => void;
    /** Resume 3D background ambient after preview stops */
    onResumeBackgroundSound?: () => void;
    /** Label ids currently assigned to the gallery (from gallery payload) */
    selectedLabelIds?: string[];
    /** After labels are saved successfully (sync client with API response) */
    onGalleryLabelsSaved?: (payload: {
        labelIds: string[];
        labels: Array<{ _id: string; name: string }>;
    }) => void;
    currentPseudoMomIntroDurationSeconds?: number;
    currentPseudoMomIntroSoundId?: string | null;
    currentPseudoMomAutoRotateObjects?: boolean;
    currentPseudoMomManualRotateObjects?: boolean;
    onPseudoMomIntroSettingsChanged?: (settings: {
        durationSeconds: number;
        introSound: PseudoMomIntroSoundSettings;
        autoRotateObjects: boolean;
        manualRotateObjects: boolean;
    }) => void;
}

const BASE_API = process.env.REACT_APP_BASE_API;
const isManualArtworkRotationFeatureEnabled = (): boolean =>
    process.env.REACT_APP_ENABLE_3D_ARTWORK_DRAG_ROTATION === "true";

const europeanaHttpSoundPlaybackUrl = (url: string): string => {
    return getProxiedSoundUrl(url, BASE_API || "");
};

export const buildEuropeanaSoundImportPayload = (sound: ExternalSound) => ({
    name: sound.name?.trim() || "Untitled",
    url: sound.url,
    sourceName: sound.sourceName,
    sourceId: sound.sourceId,
});

export const GallerySetupModal = ({ personal, canUseManagementSections, canManageCollaborators = false, galleryId, galleryOwnerId, collaborators = [], onCollaboratorsUpdated, includeUnpublishedMoods, currentMood, published, onChangePublished, moods, show, onHide, onPlaying, onMute, currentTitle, onChangeTitle, onChangeMood, canManageGallery = false, currentSoundId, currentMuted = false, onSoundChanged, onPauseBackgroundSound, onResumeBackgroundSound, selectedLabelIds = [], onGalleryLabelsSaved, currentPseudoMomIntroDurationSeconds = 5, currentPseudoMomIntroSoundId = null, currentPseudoMomAutoRotateObjects = false, currentPseudoMomManualRotateObjects = false, onPseudoMomIntroSettingsChanged, isBackgroundSoundLoading = false }: Props) => {
    const apiClient = ApiClient(BASE_API);
    const showManagementSections = canUseManagementSections ?? personal;
    const canConfigureManualArtworkRotation = isManualArtworkRotationFeatureEnabled();

    const currentUser = useSelector((state: AppState) => state.auth.user);

    const [audios, setAudios] = React.useState<HTMLAudioElement[]>([]);
    const [playingStatus, setPlayingStatus] = React.useState<boolean[]>([]);
    const [currentSoundIndex, setCurrentSoundIndex] = React.useState<number>(-1);
    const [pendingPromise, setPendingPromise] = React.useState<boolean>(false);

    const [selectedMood, setSelectedMood] = React.useState<number>(currentMood);
    const [isMoodChangeInFlight, setIsMoodChangeInFlight] = React.useState<boolean>(false);
    const [pseudoMomIntroDurationDraft, setPseudoMomIntroDurationDraft] = React.useState<string>(String(currentPseudoMomIntroDurationSeconds ?? 5));
    const [pseudoMomIntroSoundId, setPseudoMomIntroSoundId] = React.useState<string>(currentPseudoMomIntroSoundId ?? "");
    const [pseudoMomAutoRotateObjects, setPseudoMomAutoRotateObjects] = React.useState<boolean>(currentPseudoMomAutoRotateObjects ?? false);
    const [pseudoMomManualRotateObjects, setPseudoMomManualRotateObjects] = React.useState<boolean>(currentPseudoMomManualRotateObjects ?? false);
    const [pseudoMomIntroSaving, setPseudoMomIntroSaving] = React.useState<boolean>(false);

    // Collaborators state (add form, edit mode)
    const [newCollaboratorEmail, setNewCollaboratorEmail] = React.useState<string>("");
    const [newCollaboratorRole, setNewCollaboratorRole] = React.useState<"editor" | "contributor">("contributor");
    const [addCollaboratorInFlight, setAddCollaboratorInFlight] = React.useState<boolean>(false);

    React.useEffect(() => {
        setPseudoMomIntroDurationDraft(String(currentPseudoMomIntroDurationSeconds ?? 5));
        setPseudoMomIntroSoundId(currentPseudoMomIntroSoundId ?? "");
        setPseudoMomAutoRotateObjects(currentPseudoMomAutoRotateObjects ?? false);
        setPseudoMomManualRotateObjects(currentPseudoMomManualRotateObjects ?? false);
    }, [currentPseudoMomIntroDurationSeconds, currentPseudoMomIntroSoundId, currentPseudoMomAutoRotateObjects, currentPseudoMomManualRotateObjects, show]);
    const [editingCollaboratorId, setEditingCollaboratorId] = React.useState<string | null>(null);
    const [editRoleValue, setEditRoleValue] = React.useState<"editor" | "contributor">("contributor");
    const [confirmRemoveCollaborator, setConfirmRemoveCollaborator] = React.useState<{ userId: string; email: string } | null>(null);

    // Error state management (like artwork form and NewArtworkModal)
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
    
    // Track if title field has been modified to hide errors on typing
    const [titleTouched, setTitleTouched] = React.useState<boolean>(false);

    const [galleryLabelsOptions, setGalleryLabelsOptions] = React.useState<GalleryLabel[]>([]);
    const [galleryLabelsLoading, setGalleryLabelsLoading] = React.useState(false);
    const [localLabelIds, setLocalLabelIds] = React.useState<string[]>([]);
    const [labelsApplyInFlight, setLabelsApplyInFlight] = React.useState(false);

    const selectedLabelIdsKey = React.useMemo(() => [...selectedLabelIds].sort().join("|"), [selectedLabelIds]);

    // Parse errors for field-specific display (like artwork form)
    const getFieldError = (fieldName: string) => {
        if (!errors[fieldName]) return null;
        
        // Hide errors if user has started typing in the field
        if (fieldName === 'title' && titleTouched) return null;
        
        return errors[fieldName];
    };

    // Parse server errors to field-specific errors (like artwork form)
    const parseServerErrors = (error: any) => {
        const fieldErrors: { [key: string]: string } = {};
        
        if (!error) return fieldErrors;
        
        // Check if error has field-specific validation errors
        if (typeof error === 'object' && error) {
            // Handle validation errors object
            if (error.errors) {
                Object.keys(error.errors).forEach(field => {
                    const fieldError = error.errors[field];
                    if (typeof fieldError === 'string') {
                        fieldErrors[field] = fieldError;
                    } else if (fieldError.message) {
                        fieldErrors[field] = fieldError.message;
                    } else if (Array.isArray(fieldError)) {
                        fieldErrors[field] = fieldError[0]; // Take first error if array
                    }
                });
            }
            
            // Handle response.data structure (if error is nested in response)
            if (error.response && error.response.data) {
                return parseServerErrors(error.response.data);
            }
            
            // Handle direct field errors
            if (error.newTitle) fieldErrors.title = typeof error.newTitle === 'string' ? error.newTitle : error.newTitle.message || 'Invalid title';
            if (error.name) fieldErrors.title = typeof error.name === 'string' ? error.name : error.name.message || 'Invalid gallery name';
            
            // Handle msg field (common in your app)
            if (error.msg && !Object.keys(fieldErrors).length) {
                fieldErrors.general = error.msg;
            }
        }
        
        // If it's a string error, try to parse as JSON
        if (typeof error === 'string') {
            try {
                const errorObj = JSON.parse(error);
                return parseServerErrors(errorObj);
            } catch {
                // If not JSON, return as general error
                fieldErrors.general = error;
            }
        }
        
        return fieldErrors;
    };

    // Reset touched states when new errors come in
    React.useEffect(() => {
        if (Object.keys(errors).length > 0) {
            setTitleTouched(false);
        }
    }, [errors]);

    const visibleMoodEntries = React.useMemo(() => {
        return getVisibleMoodEntries(moods, {
            userId: currentUser?._id,
            galleryId,
            includeUnpublished: includeUnpublishedMoods,
            includeAssignedUnpublished: true
        });
    }, [moods, currentUser?._id, galleryId, includeUnpublishedMoods]);

    const moodEntriesForDropdown = React.useMemo(() => {
        const entriesByIndex = new Map<number, { mood: any; index: number; audienceType: string }>();

        visibleMoodEntries.forEach((entry) => {
            entriesByIndex.set(entry.index, {
                mood: entry.mood,
                index: entry.index,
                audienceType: entry.audienceType
            });
        });

        moods.forEach((mood, index) => {
            if (!mood) return;
            const audienceType = getMoodAudienceType(mood);
            if (audienceType !== "general") return;

            if (!entriesByIndex.has(index)) {
                entriesByIndex.set(index, {
                    mood,
                    index,
                    audienceType
                });
            }
        });

        return Array.from(entriesByIndex.values())
            .sort((a, b) => a.index - b.index)
            .map((entry) => ({
                ...entry
            }));
    }, [visibleMoodEntries, moods, currentMood]);

    React.useEffect(() => {
        if (moodEntriesForDropdown.length === 0) {
            setSelectedMood(-1);
            return;
        }

        const hasCurrentMood = moodEntriesForDropdown.some((entry) => entry.index === currentMood);
        if (hasCurrentMood) {
            setSelectedMood(currentMood);
            return;
        }

        setSelectedMood(moodEntriesForDropdown[0].index);
    }, [currentMood, moodEntriesForDropdown]);

    // Build display list: owner + collaborators (from props)
    const collaboratorsDisplayList = React.useMemo<CollaboratorRow[]>(() => {
        const ownerRow: CollaboratorRow | null = galleryOwnerId && currentUser
            ? {
                userId: galleryOwnerId,
                email: currentUser._id === galleryOwnerId ? (currentUser.email || "You") : "Owner",
                role: "owner",
            }
            : null;
        const collabList = (collaborators || []).map((c) => ({
            userId: c.userId,
            email: c.email,
            role: c.role as "editor" | "contributor",
        }));
        return ownerRow ? [ownerRow, ...collabList] : collabList;
    }, [galleryOwnerId, currentUser?._id, currentUser?.email, collaborators]);

    const successNotify = (msg: string) => {
        toast.success(msg, {
            position: "bottom-left"
        })
    }

    const failedNotify = (msg = "Failed to save change") => {
        toast.warn(msg, {
            position: "bottom-left"
        })
    }

    const onAddCollaborator = async () => {
        if (!newCollaboratorEmail.trim() || addCollaboratorInFlight) return;
        setAddCollaboratorInFlight(true);
        const { status, error } = await apiClient.addGalleryCollaborator(galleryId, newCollaboratorEmail.trim(), newCollaboratorRole);
        if (status) {
            successNotify("Collaborator added");
            setNewCollaboratorEmail("");
            onCollaboratorsUpdated?.();
        } else {
            toast.error((error as any)?.msg || "Failed to add collaborator", { position: "bottom-left" });
        }
        setAddCollaboratorInFlight(false);
    };

    const onUpdateCollaboratorRole = async (userId: string, role: "editor" | "contributor") => {
        const { status } = await apiClient.updateGalleryCollaboratorRole(galleryId, userId, role);
        if (status) {
            successNotify("Role updated");
            setEditingCollaboratorId(null);
            onCollaboratorsUpdated?.();
        } else {
            failedNotify();
        }
    };

    const onRemoveCollaborator = async (userId: string) => {
        const { status } = await apiClient.removeGalleryCollaborator(galleryId, userId);
        if (status) {
            successNotify("Collaborator removed");
            setEditingCollaboratorId(null);
            onCollaboratorsUpdated?.();
        } else {
            failedNotify();
        }
    };

    const [selectedColor, setSelectedColor] = React.useState<number>(0);
    const [selectedAmbient, setSelectedAmbient] = React.useState<number>(0);

    // Gallery sounds (from GET /sounds/:galleryId)
    const [gallerySounds, setGallerySounds] = React.useState<Sounds[]>([]);
    const [soundsLoading, setSoundsLoading] = React.useState<boolean>(false);
    const [soundChangeInFlight, setSoundChangeInFlight] = React.useState<boolean>(false);
    const [addSoundInFlight, setAddSoundInFlight] = React.useState<boolean>(false);
    const [confirmRemoveSound, setConfirmRemoveSound] = React.useState<Sounds | null>(null);
    const [newSoundName, setNewSoundName] = React.useState<string>("");
    const [newSoundFile, setNewSoundFile] = React.useState<File | null>(null);
    const [soundEditMode, setSoundEditMode] = React.useState<boolean>(false);
    const [selectedSoundId, setSelectedSoundId] = React.useState<string | undefined>(currentSoundId);
    const [selectedMuted, setSelectedMuted] = React.useState<boolean>(currentMuted ?? false);
    const addSoundInputRef = React.useRef<HTMLInputElement>(null);
    
    // Maybe we can change Europeana into external for other providers
    const europeanaAudioRefs = React.useRef<Map<string, HTMLAudioElement>>(new Map());

    const [europeanaQuery, setEuropeanaQuery] = React.useState("");
    const [europeanaResults, setEuropeanaResults] = React.useState<ExternalSound[]>([]);
    const [europeanaLoading, setEuropeanaLoading] = React.useState(false);
    const [europeanaPage, setEuropeanaPage] = React.useState(1);
    const [europeanaPageSize, setEuropeanaPageSize] = React.useState(12);
    const [europeanaTotalResults, setEuropeanaTotalResults] = React.useState(0);
    const [europeanaImportingId, setEuropeanaImportingId] = React.useState<string | null>(null);
    const [europeanaHasSearched, setEuropeanaHasSearched] = React.useState(false);

    // Sync selected sound and muted state from parent when modal opens or props change
    React.useEffect(() => {
        setSelectedSoundId(currentSoundId);
        setSelectedMuted(currentMuted ?? false);
    }, [show, currentSoundId, currentMuted]);

    // Reset edit mode when modal closes
    React.useEffect(() => {
        if (!show) setSoundEditMode(false);
    }, [show]);

    React.useEffect(() => {
        if (!show) {
            europeanaAudioRefs.current.forEach((a) => a.pause());
            europeanaAudioRefs.current.clear();
            setEuropeanaResults([]);
            setEuropeanaPage(1);
            setEuropeanaTotalResults(0);
            setEuropeanaQuery("");
            setEuropeanaHasSearched(false);
        }
    }, [show]);

    // Fetch gallery sounds when modal opens
    React.useEffect(() => {
        if (show && galleryId && showManagementSections) {
            (async () => {
                setSoundsLoading(true);
                const resp = await apiClient.getSoundsForGallery(galleryId);
                setSoundsLoading(false);
                if (resp?.status && resp?.sounds?.length) {
                    setGallerySounds(resp.sounds);
                } else {
                    setGallerySounds([]);
                }
            })();
        }
    }, [show, galleryId, showManagementSections]);

    React.useEffect(() => {
        if (!show || !galleryId) return;
        (async () => {
            setGalleryLabelsLoading(true);
            const resp = await apiClient.listGalleryLabels();
            setGalleryLabelsLoading(false);
            if (resp.status && Array.isArray(resp.labels)) {
                setGalleryLabelsOptions(resp.labels as GalleryLabel[]);
            } else {
                setGalleryLabelsOptions([]);
            }
        })();
    }, [show, galleryId]);

    React.useEffect(() => {
        if (show) {
            setLocalLabelIds([...selectedLabelIds]);
        }
    }, [show, selectedLabelIdsKey]);

    const galleryLabelSelectOptions = React.useMemo<TagOption[]>(
        () => galleryLabelsOptions.map((l) => ({ value: l._id, label: l.name })),
        [galleryLabelsOptions]
    );

    const galleryLabelSelectValue = React.useMemo(() => {
        const byId = new Map(galleryLabelSelectOptions.map((o) => [o.value, o]));
        return localLabelIds.map((id) => byId.get(id)).filter(Boolean) as TagOption[];
    }, [localLabelIds, galleryLabelSelectOptions]);

    const persistGalleryLabels = async (labelIds: string[]) => {
        if (!canManageGallery || !galleryId) return;
        setLabelsApplyInFlight(true);
        const resp = await apiClient.setGalleryLabels(galleryId, labelIds);
        setLabelsApplyInFlight(false);
        if (resp.status) {
            const labels = resp.labels as Array<{ _id: string; name: string }>;
            const resolvedIds = labels.map((l) => String(l._id));
            onGalleryLabelsSaved?.({ labelIds: resolvedIds, labels });
            successNotify("Gallery labels updated");
        } else {
            failedNotify();
            setLocalLabelIds([...selectedLabelIds]);
        }
    };

    const onGalleryLabelSelectChange = (selected: MultiValue<TagOption>) => {
        const nextIds = selected ? selected.map((o) => o.value) : [];
        setLocalLabelIds(nextIds);
        if (!canManageGallery || !galleryId) return;
        void persistGalleryLabels(nextIds);
    };

    const displaySounds = gallerySounds;
    const selectedSoundIndex = displaySounds.findIndex(s => s._id === (selectedSoundId ?? currentSoundId));
    const selectedSoundIdx = selectedSoundIndex >= 0 ? selectedSoundIndex : 0;

    React.useEffect(() => {
        const _audios = displaySounds.map((s: Sounds) => {
            const url = s.url || "";
            const playbackUrl = getProxiedSoundUrl(url, BASE_API || "");
            return playbackUrl ? new Audio(playbackUrl) : new Audio();
        });
        setAudios(_audios);
        setPlayingStatus(_audios.map(() => false)); 
        return () => {
            for (const a of _audios) {
                a.pause();
            }
        };
    }, [displaySounds])

    const pauseOtherEuropeanaPreviews = (exceptId: string) => {
        europeanaAudioRefs.current.forEach((a, id) => {
            if (id !== exceptId) {
                a.pause();
                a.currentTime = 0;
            }
        });
    };

    const pauseAllEuropeanaPreviews = () => {
        europeanaAudioRefs.current.forEach((a) => {
            a.pause();
            a.currentTime = 0;
        });
    };

    const scheduleResumeBackgroundIfNoPreviewsPlaying = () => {
        requestAnimationFrame(() => {
            const anyEuropeana = Array.from(europeanaAudioRefs.current.values()).some((a) => !a.paused);
            const anyGallery = audios.some((a) => a && !a.paused);
            if (!anyEuropeana && !anyGallery) {
                onResumeBackgroundSound?.();
            }
        });
    };

    const onPlayAudio = (i: number) => {
        if (pendingPromise) {
            return;
        }

        // check play different sound
        if (currentSoundIndex !== i) {
            // different one
            pauseAllEuropeanaPreviews();
            onPauseBackgroundSound?.();
            for (const a of audios) {
                a.pause();
                a.currentTime = 0;
            }

            const playPromise = audios[i].play();
            setPendingPromise(true);
            if (playPromise !== undefined) {
                playPromise.then(_ => {
                    console.log("fulfilled")
                    setPendingPromise(false);
                    onPlaying(true);
                })
            }

            setPlayingStatus(playingStatus.map((p, idx) => {
                if (idx === i) {
                    return true
                }
                return false
            }))
        } else {
            if (playingStatus[i]) {
                audios[i].pause();
                onPlaying(false);
                scheduleResumeBackgroundIfNoPreviewsPlaying();
            } else {
                pauseAllEuropeanaPreviews();
                onPauseBackgroundSound?.();
                audios[i].play();
                onPlaying(true);
            }

            setPlayingStatus(playingStatus.map((p, idx) => {
                if (idx === i) {
                    return !p;
                }
                return p;
            }))
        }
        setCurrentSoundIndex(i);
    }

    const runEuropeanaSearch = async (page: number, pageSizeOverride?: number) => {
        const q = europeanaQuery.trim();
        if (!q || !canManageGallery || !showManagementSections) return;
        setEuropeanaHasSearched(true);
        const size = pageSizeOverride ?? europeanaPageSize;
        if (pageSizeOverride != null) {
            setEuropeanaPageSize(pageSizeOverride);
        }
        setEuropeanaLoading(true);
        pauseAllEuropeanaPreviews();
        scheduleResumeBackgroundIfNoPreviewsPlaying();
        const resp = await apiClient.searchEuropeanaSounds({
            query: q,
            page,
            pageSize: size,
        });
        setEuropeanaLoading(false);
        if (!resp.status) {
            toast.warn("Europeana search failed", { position: "bottom-left" });
            return;
        }

        setEuropeanaResults(resp.sounds);
        setEuropeanaTotalResults(resp.totalResults ?? 0);
        setEuropeanaPage(page);
    };

    const europeanaTotalPages = Math.max(1, Math.ceil(europeanaTotalResults / Math.max(1, europeanaPageSize)));

    const onClearEuropeanaSearch = () => {
        pauseAllEuropeanaPreviews();
        europeanaAudioRefs.current.clear();
        scheduleResumeBackgroundIfNoPreviewsPlaying();
        setEuropeanaQuery("");
        setEuropeanaResults([]);
        setEuropeanaPage(1);
        setEuropeanaTotalResults(0);
        setEuropeanaHasSearched(false);
    };

    const onEuropeanaUseAsBackground = async (sound: ExternalSound) => {
        if (!canManageGallery || europeanaImportingId || isBackgroundSoundLoading) return;
        const importPayload = buildEuropeanaSoundImportPayload(sound);
        setSoundChangeInFlight(true)
        setEuropeanaImportingId(sound.sourceId);
        try {
            const importResp = await apiClient.importEuropeanaSoundForGallery(
                galleryId,
                importPayload
            );
            setEuropeanaImportingId(null);
            if (!importResp.status) {
                failedNotify();
                setSoundChangeInFlight(false);
                setEuropeanaImportingId(null);
                return;
            }

            const refresh = await apiClient.getSoundsForGallery(galleryId);
            const list = refresh.sounds;
            if (refresh.status && list.length) {
                setGallerySounds(list);
            }

            let matched = list.find((s) => s.sourceId === sound.sourceId);

            if (!matched) {
                const gResp = await apiClient.getGalleryById(galleryId);
                const gallery = (gResp as { gallery?: { data?: { sound?: { soundId?: string; url?: string } } } })
                    ?.gallery;
                const sid = gallery?.data?.sound?.soundId;
                const gallerySoundUrl = gallery?.data?.sound?.url;
                if (sid) {
                    matched = list.find((s) => s._id === sid);
                }
                if (!matched && sid && gallerySoundUrl) {
                    matched = {
                        _id: sid,
                        name: importPayload.name,
                        url: gallerySoundUrl,
                        public: false,
                    };
                }
            }

            if (matched?._id) {
                setSelectedSoundId(matched._id);

                // pause all europeana previews
                pauseAllEuropeanaPreviews();
                await onSoundChanged(matched);
                successNotify("Sound has been changed");
            }
        } catch (_error) {
            console.error("Gallery sound change failed");
            failedNotify();
        } finally { 
            setSoundChangeInFlight(false);
            setEuropeanaImportingId(null);
        }
    };

    const onChangeSoundForGallery = async (soundId: string) => {
        if (soundChangeInFlight || !canManageGallery || isBackgroundSoundLoading) return;
        setSoundChangeInFlight(true);

        try {
            const resp = await apiClient.changeSoundsForGallery(galleryId, soundId);
            if (resp?.status) {
                setSelectedSoundId(soundId);
                const sound = displaySounds.find(s => s._id === soundId);
                await onSoundChanged(sound);
                successNotify("Sound has been changed");
            } else {
                failedNotify();
            }
        } catch (_error) {
            console.error("Gallery sound change failed");
            failedNotify();
        } finally {
            setSoundChangeInFlight(false);
        }
    };

    const onSoundSelectChange: React.ChangeEventHandler<HTMLSelectElement> = async (e) => {
        const soundId = e.target.value;
        if (!soundId) return;
        await onChangeSoundForGallery(soundId);
    };

    const onAddSoundSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newSoundName.trim() || !newSoundFile || addSoundInFlight || !canManageGallery) return;
        setAddSoundInFlight(true);
        const formData = new FormData();
        formData.append("name", newSoundName.trim());
        formData.append("sound", newSoundFile);
        const resp = await apiClient.createSoundForGallery(galleryId, formData);
        setNewSoundName("");
        setNewSoundFile(null);
        if (addSoundInputRef.current) addSoundInputRef.current.value = "";
        if (resp.status) {
            const newSound = resp.sound;
            if (newSound._id) {
                setGallerySounds(prev => [...prev, newSound]);
            } else {
                const refresh = await apiClient.getSoundsForGallery(galleryId);
                if (refresh.status && refresh.sounds.length) {
                    setGallerySounds(refresh.sounds);
                }
            }
            successNotify("Sound has been added");
        } else {
            failedNotify();
        }
        setAddSoundInFlight(false);
    };

    const onAddSoundFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        setNewSoundFile(file || null);
    };

    const onRemoveSound = async (sound: Sounds) => {
        if (sound._id === selectedSoundId) {
            failedNotify("Cannot remove the current background sound");
            return;
        }

        if (!canManageGallery) return;
        setConfirmRemoveSound(sound);
    };

    const onConfirmRemoveSound = async () => {
        if (!confirmRemoveSound) return;
        const resp = await apiClient.removeSoundFromGallery(galleryId, confirmRemoveSound._id);
        setConfirmRemoveSound(null);
        if (resp.status) {
            setGallerySounds(prev => prev.filter(s => s._id !== confirmRemoveSound._id));
            successNotify("Sound has been removed");
        } else {
            failedNotify();
        }
    };

    // if user is editor, they can remove the sound
    const canRemoveSound = (sound: Sounds) => !sound.public && (sound.owner === currentUser?._id || canManageGallery);

    const onChangeMoodForGallery = async (newMood?: number) => {
        if (isMoodChangeInFlight) return;
        const moodToApply = typeof newMood === "number" ? newMood : selectedMood;
        if (moodToApply < 0) {
            failedNotify();
            return;
        }
        setIsMoodChangeInFlight(true);
        try {
            const resp = await apiClient.changeMoodForGallery(galleryId, moodToApply);
            if (resp.status) {
                await onChangeMood(moodToApply);
                successNotify("Mood has been modified");
            } else {
                failedNotify();
            }
        } finally {
            setIsMoodChangeInFlight(false);
        }
    }

    const onMoodSelectChange: React.ChangeEventHandler<HTMLSelectElement> = async (e) => {
        const newValue = parseInt(e.target.value, 10);
        if (Number.isNaN(newValue)) return;
        setSelectedMood(newValue);
        await onChangeMoodForGallery(newValue);
    };

    const [muteInFlight, setMuteInFlight] = React.useState<boolean>(false);
    const onToggleMute = async () => {
        if (muteInFlight || soundChangeInFlight) return;
        const newMuted = !selectedMuted;
        setMuteInFlight(true);
        try {
            const resp = await apiClient.muteGallery(galleryId, newMuted);
            if (resp?.status) {
                setSelectedMuted(newMuted);
                await onMute(newMuted);
                successNotify(newMuted ? "Background sound muted" : "Background sound unmuted");
            } else {
                failedNotify();
            } 
        }
        catch (error) {
            console.error("Error toggling mute:", error);
            failedNotify();
        } finally {
            setMuteInFlight(false);
        }
        
        
    };

    const onSetSpawningPoint = async () => {
        // load roration and position from storage 
        const storedPosition = localStorage.getItem("CameraPosition");
        const { capsulePosition, cameraRotation } = JSON.parse(storedPosition);

        const position = {
            x: capsulePosition.x,
            y: capsulePosition.y,
            z: capsulePosition.z
        }

        const rotation = {
            x: cameraRotation.x,
            y: cameraRotation.y,
            z: 0
        }

        const { status } = await apiClient.setSpawningPoint(galleryId, position, rotation);
        if (status) {
            successNotify("start point has been changed");
        } else {
            failedNotify();
        }
    }

    const onPublish = async () => {
        if (labelsApplyInFlight) return;
        const { status } = await apiClient.publishGallery(galleryId, !published);
        if (status) {
            onChangePublished(!published);
            successNotify(`Gallery has been ${published ? "unpublished" : "published"}`);
        } else {
            failedNotify();
        }
    }

    const applyTitle = async () => {
        setErrors({});
        const { status, error } = await apiClient.changeGalleryTitle(galleryId, currentTitle);
        if (status) {
            successNotify("Gallery name has been changed");
        } else {
            
            // Parse and set field-specific errors
            const fieldErrors = parseServerErrors(error);
            console.error("Error during title change:", error);
            setErrors(fieldErrors);
            
            // If there are field-specific errors, show them inline
            if (Object.keys(fieldErrors).length > 0) {
                const generalError = fieldErrors.general || "Please check the form for errors";
                toast.error(generalError, { position: "bottom-left" });
            } else {
                // Fallback to original error handling
                failedNotify();
            }
        }
    }

    const parsedPseudoMomIntroDuration = React.useMemo(() => {
        const trimmedValue = pseudoMomIntroDurationDraft.trim();
        if (trimmedValue === "") return null;
        const value = Number(trimmedValue);
        if (!Number.isFinite(value)) return null;
        const roundedValue = Math.round(value);
        if (roundedValue < 0 || roundedValue > 30) return null;
        return roundedValue;
    }, [pseudoMomIntroDurationDraft]);

    const applyPseudoMomIntroSettings = async () => {
        if (pseudoMomIntroSaving) return;
        if (parsedPseudoMomIntroDuration === null) {
            failedNotify("Loading intro duration must be between 0 and 30 seconds");
            return;
        }
        setPseudoMomIntroSaving(true);
        const response = await apiClient.updatePseudoMomIntroSettings(galleryId, {
            durationSeconds: parsedPseudoMomIntroDuration,
            introSoundId: pseudoMomIntroSoundId || null,
            autoRotateObjects: pseudoMomAutoRotateObjects,
            manualRotateObjects: pseudoMomManualRotateObjects
        });
        if (response?.status) {
            const savedGallery = response.data?.gallery || response.gallery || {};
            const savedDuration = savedGallery.pseudoMomIntroDurationSeconds ?? parsedPseudoMomIntroDuration;
            const savedIntroSound = savedGallery.pseudoMomIntroSound ?? null;
            const savedAutoRotateObjects = savedGallery.pseudoMomAutoRotateObjects ?? pseudoMomAutoRotateObjects;
            const savedManualRotateObjects = savedGallery.pseudoMomManualRotateObjects ?? pseudoMomManualRotateObjects;
            setPseudoMomIntroDurationDraft(String(savedDuration));
            setPseudoMomIntroSoundId(savedIntroSound?.soundId ?? "");
            setPseudoMomAutoRotateObjects(savedAutoRotateObjects);
            setPseudoMomManualRotateObjects(savedManualRotateObjects);
            onPseudoMomIntroSettingsChanged?.({
                durationSeconds: savedDuration,
                introSound: savedIntroSound,
                autoRotateObjects: savedAutoRotateObjects,
                manualRotateObjects: savedManualRotateObjects
            });
            successNotify("Gallery display settings have been updated");
        } else {
            failedNotify(response?.error?.message || "Failed to update loading intro settings");
        }
        setPseudoMomIntroSaving(false);
    }

    return (
        <>
            <Modal show={show} onHide={onHide} centered size="lg" className="gallery-settings-modal">
                <Modal.Header closeButton id="gallerySettingsModalLabel">
                    <Modal.Title className="fs-4 fw-bold">Gallery Settings</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4" id="modal_body">
                    <h6 className="fw-semibold mb-3 text-success d-flex align-items-center">
                        <Images className="me-2" /> Gallery Information
                    </h6>
                    <div id="gallery_info_section" className="mb-4">
                        <div className="mb-3">
                            <label className="form-label">Rename Gallery</label>
                            <div className="mb-3 d-flex">
                                <input
                                    type="text"
                                    className={`form-control form-control-lg me-2 ${getFieldError('title') ? 'is-invalid' : ''}`}
                                    placeholder="My Awesome Gallery"
                                    value={currentTitle}
                                    onChange={(e) => {
                                        onChangeTitle(e.target.value);
                                        setTitleTouched(true);
                                    }}
                                />
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary flex-grow-1 py-2"
                                    onClick={applyTitle}
                                >
                                    Apply
                                </button>
                            </div>
                            {getFieldError('title') && (
                                <div className="text-danger small mt-1">{getFieldError('title')}</div>
                            )}
                        </div>
                        <div className="mb-3">
                            <label className="form-label" htmlFor="gallery-setup-labels">
                                Gallery labels
                            </label>
                            {galleryLabelsLoading ? (
                                <div className="text-muted small">Loading labels...</div>
                            ) : galleryLabelSelectOptions.length === 0 ? (
                                <div className="text-muted small">No labels available yet.</div>
                            ) : (
                                <div style={{ minWidth: 0 }}>
                                        <Select<TagOption, true>
                                        inputId="gallery-setup-labels"
                                        instanceId="gallery-setup-labels"
                                        name="galleryLabels"
                                        className="basic-multi-select"
                                        classNamePrefix="select"
                                        isMulti
                                        options={galleryLabelSelectOptions}
                                        value={galleryLabelSelectValue}
                                        onChange={onGalleryLabelSelectChange}
                                        placeholder="Select labels..."
                                        isDisabled={!canManageGallery || labelsApplyInFlight}
                                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                        menuPosition="fixed"
                                        styles={{
                                            ...galleryLabelTagStylesBase,
                                            control: (styles, { isFocused }) => ({
                                                ...styles,
                                                backgroundColor: "var(--brand-dark)",
                                                color: "var(--brand-light-gray)",
                                                borderRadius: "0.375rem",
                                                minHeight: "38px",
                                                border: isFocused
                                                    ? "1px solid var(--brand-green)"
                                                    : "1px solid var(--brand-dark-3)",
                                                boxShadow: isFocused
                                                    ? "0 0 0 0.25rem rgba(16, 185, 129, 0.25)"
                                                    : "none",
                                                "&:hover": {
                                                    border: "1px solid var(--brand-green)",
                                                },
                                            }),
                                            menuPortal: (base) => ({ ...base, zIndex: 2000 }),
                                            menu: (base) => ({
                                                ...base,
                                                backgroundColor: "var(--brand-dark-2)",
                                                color: "var(--brand-light-gray)",
                                                border: "1px solid var(--brand-dark-3)",
                                            }),
                                            menuList: (base) => ({
                                                ...base,
                                                backgroundColor: "var(--brand-dark-2)",
                                            }),
                                            option: (styles, { isFocused, isSelected, isDisabled }) => ({
                                                ...styles,
                                                backgroundColor: isSelected
                                                    ? "var(--brand-green)"
                                                    : isFocused
                                                      ? "var(--brand-dark-3)"
                                                      : "var(--brand-dark-2)",
                                                color: isSelected ? "#ffffff" : "var(--brand-light-gray)",
                                                cursor: isDisabled ? "not-allowed" : "pointer",
                                                ":active": {
                                                    ...styles[":active"],
                                                    backgroundColor: isSelected
                                                        ? "var(--brand-green)"
                                                        : "var(--brand-dark-3)",
                                                },
                                            }),
                                            placeholder: (styles) => ({
                                                ...styles,
                                                color: "var(--brand-gray)",
                                            }),
                                            input: (styles) => ({
                                                ...styles,
                                                color: "var(--brand-light-gray)",
                                            }),
                                            singleValue: (styles) => ({
                                                ...styles,
                                                color: "var(--brand-light-gray)",
                                            }),
                                            multiValue: (styles) => ({
                                                ...styles,
                                                backgroundColor: "var(--brand-dark-3)",
                                            }),
                                            multiValueLabel: (styles) => ({
                                                ...styles,
                                                color: "var(--brand-light-gray)",
                                            }),
                                            multiValueRemove: (styles) => ({
                                                ...styles,
                                                color: "var(--brand-light-gray)",
                                                ":hover": {
                                                    backgroundColor: "var(--brand-dark)",
                                                    color: "#ffffff",
                                                },
                                            }),
                                            dropdownIndicator: (styles) => ({
                                                ...styles,
                                                color: "var(--brand-gray)",
                                                ":hover": { color: "var(--brand-light-gray)" },
                                            }),
                                            clearIndicator: (styles) => ({
                                                ...styles,
                                                color: "var(--brand-gray)",
                                                ":hover": { color: "var(--brand-light-gray)" },
                                            }),
                                            indicatorSeparator: (styles) => ({
                                                ...styles,
                                                backgroundColor: "var(--brand-dark-3)",
                                            }),
                                            valueContainer: (styles) => ({
                                                ...styles,
                                                color: "var(--brand-light-gray)",
                                            }),
                                        }}
                                        />
                                </div>
                            )}
                        </div>
                        {canManageGallery && (
                            <div className="mb-3 border rounded p-3">
                                <div className="row g-3 align-items-end">
                                    <div className="col-md-4">
                                        <label className="form-label" htmlFor="pseudo-mom-intro-duration">
                                            Loading intro duration
                                        </label>
                                        <input
                                            id="pseudo-mom-intro-duration"
                                            type="number"
                                            min={0}
                                            max={30}
                                            step={1}
                                            className={`form-control form-control-lg ${parsedPseudoMomIntroDuration === null ? "is-invalid" : ""}`}
                                            value={pseudoMomIntroDurationDraft}
                                            onChange={(event) => setPseudoMomIntroDurationDraft(event.target.value)}
                                            onBlur={() => {
                                                if (parsedPseudoMomIntroDuration === null) {
                                                    setPseudoMomIntroDurationDraft(String(currentPseudoMomIntroDurationSeconds ?? 5));
                                                }
                                            }}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label" htmlFor="pseudo-mom-intro-sound">
                                            Loading intro sound
                                        </label>
                                        <select
                                            id="pseudo-mom-intro-sound"
                                            className="form-select form-select-lg"
                                            value={pseudoMomIntroSoundId}
                                            onChange={(event) => setPseudoMomIntroSoundId(event.target.value)}
                                            disabled={soundsLoading || pseudoMomIntroSaving}
                                        >
                                            <option value="">No loading sound</option>
                                            {displaySounds.map((sound) => (
                                                <option key={sound._id} value={sound._id}>
                                                    {sound.name || sound._id}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-4 d-flex flex-column gap-2">
                                        <div className="form-check">
                                            <input
                                                id="pseudo-mom-auto-rotate"
                                                className="form-check-input"
                                                type="checkbox"
                                                checked={pseudoMomAutoRotateObjects}
                                                onChange={(event) => setPseudoMomAutoRotateObjects(event.target.checked)}
                                                disabled={pseudoMomIntroSaving}
                                            />
                                            <label className="form-check-label" htmlFor="pseudo-mom-auto-rotate">
                                                Auto rotate objects
                                            </label>
                                        </div>
                                        {canConfigureManualArtworkRotation && (
                                            <div className="form-check">
                                                <input
                                                    id="pseudo-mom-manual-rotate"
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={pseudoMomManualRotateObjects}
                                                    onChange={(event) => setPseudoMomManualRotateObjects(event.target.checked)}
                                                    disabled={pseudoMomIntroSaving}
                                                />
                                                <label className="form-check-label" htmlFor="pseudo-mom-manual-rotate">
                                                    Manually rotate objects
                                                </label>
                                            </div>
                                        )}
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary py-2"
                                            onClick={applyPseudoMomIntroSettings}
                                            disabled={pseudoMomIntroSaving || parsedPseudoMomIntroDuration === null}
                                        >
                                            {pseudoMomIntroSaving ? "Applying..." : "Apply"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="d-flex gap-3">
                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip>Set your current view as the starting point for all visitors entering your gallery</Tooltip>}
                            >
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary flex-grow-1 py-2"
                                    onClick={onSetSpawningPoint}
                                >
                                    Change Spawning Point
                                </button>
                            </OverlayTrigger>

                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip>Publish your gallery to make it visible to all visitors in the Exhibitions section</Tooltip>}
                            >
                                <button
                                    type="button"
                                    className="btn btn-primary flex-grow-1 py-2"
                                    onClick={onPublish}
                                    disabled={labelsApplyInFlight}
                                >
                                    {published ? "Unpublish Gallery" : "Publish Gallery"}
                                </button>
                            </OverlayTrigger>
                        </div>
                    </div>

                    <div className="border-top my-4" />

                    <div id="ambiance_section" className="mb-2">
                        <h6 className="fw-semibold mb-3 text-success d-flex align-items-center">
                            <Sun className="me-2" /> Ambiance Settings
                        </h6>
                        <label className="form-label">Mood</label>
                        <select
                            className="form-select form-select-lg"
                            value={selectedMood}
                            onChange={onMoodSelectChange}
                            disabled={isMoodChangeInFlight || moodEntriesForDropdown.length === 0}
                        >
                            {moodEntriesForDropdown.length === 0 && <option value={-1}>No moods available</option>}
                            {moodEntriesForDropdown.map(({ mood, index }) => (
                                <option key={index} value={index}>
                                    {mood.name}
                                </option>
                            ))}
                        </select>
                        
                        <label className="form-label mt-3">Background sound</label>
                        {soundsLoading ? (
                            <div className="text-muted small">Loading sounds...</div>
                        ) : (
                            <div className="d-flex flex-column gap-2">
                                <div className="d-flex gap-2 align-items-center">
                                    <select
                                        className="form-select form-select-lg flex-grow-1"
                                        value={selectedSoundId || currentSoundId || (displaySounds[selectedSoundIdx]?._id ?? "")}
                                        onChange={onSoundSelectChange}
                                        disabled={muteInFlight || !canManageGallery || soundChangeInFlight || displaySounds.length === 0 || isBackgroundSoundLoading}
                                    >
                                        {displaySounds.length === 0 && <option value="">No sounds available</option>}
                                        {displaySounds.map((s) => (
                                            <option key={s._id} value={s._id}>
                                                {s.name || s._id}
                                            </option>
                                        ))}
                                    </select>
                                    {canManageGallery && (
                                        <>
                                            {soundEditMode ? (
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={<Tooltip>Hide</Tooltip>}
                                                >
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-secondary p-2"
                                                        onClick={() => setSoundEditMode(false)}
                                                        aria-label="Hide"
                                                    >
                                                        <EyeSlash size={16} />
                                                    </button>
                                                </OverlayTrigger>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary p-2"
                                                    onClick={() => setSoundEditMode(true)}
                                                    aria-label="Edit sounds"
                                                >
                                                    <Pencil size={16} />
                                                </button>
                                            )}
                                            {displaySounds.length > 0 && (
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={<Tooltip>{selectedMuted ? "Unmute" : "Mute"} background sound</Tooltip>}
                                                >
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-secondary p-2"
                                                        onClick={onToggleMute}
                                                        disabled={muteInFlight || soundChangeInFlight || isBackgroundSoundLoading}
                                                        aria-label={selectedMuted ? "Unmute" : "Mute"}
                                                    >
                                                        {selectedMuted ? <VolumeMuteFill size={16} /> : <VolumeUpFill size={16} />}
                                                    </button>
                                                </OverlayTrigger>
                                            )}
                                        </>
                                    )}
                                </div>
                                {canManageGallery && soundEditMode && (
                                    <>
                                        <form onSubmit={onAddSoundSubmit} className="add-sound-form d-flex flex-column gap-2 p-3 border rounded">
                                            <label className="form-label mb-0">Add new sound</label>
                                            <div className="row g-2">
                                                <div className="col-md-6">
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-lg"
                                                        placeholder="Sound name"
                                                        value={newSoundName}
                                                        onChange={(e) => setNewSoundName(e.target.value)}
                                                        disabled={addSoundInFlight}
                                                    />
                                                </div>
                                                <div className="col-md-6 d-flex gap-2 align-items-center">
                                                    <input
                                                        ref={addSoundInputRef}
                                                        type="file"
                                                        accept="audio/*"
                                                        onChange={onAddSoundFileChange}
                                                        disabled={addSoundInFlight}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-secondary py-2"
                                                        onClick={() => addSoundInputRef.current?.click()}
                                                        disabled={addSoundInFlight}
                                                    >
                                                        Choose file
                                                    </button>
                                                    <span className="add-sound-filename text-truncate flex-grow-1" style={{ maxWidth: "140px" }} title={newSoundFile?.name}>
                                                        {newSoundFile ? newSoundFile.name : "No file chosen"}
                                                    </span>
                                                </div>
                                            </div>
                                            <button
                                                type="submit"
                                                className="btn btn-outline-secondary py-2 align-self-start"
                                                disabled={addSoundInFlight || !newSoundName.trim() || !newSoundFile}
                                            >
                                                {addSoundInFlight ? "Adding..." : <><Plus size={14} className="me-1" />Add sound</>}
                                            </button>
                                        </form>
                                        {(() => {
                                            const deletableSounds = displaySounds.filter(canRemoveSound);
                                            if (deletableSounds.length === 0) return null;
                                            return (
                                                <div className="table-responsive rounded-3 overflow-hidden border collaborators-table">
                                                    <table className="table table-sm table-hover mb-0">
                                                        <thead>
                                                            <tr>
                                                                <th className="ps-3">Sound</th>
                                                                <th className="text-end pe-3" style={{ width: "60px" }}>Actions</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {deletableSounds.map((s) => (
                                                                <tr key={s._id}>
                                                                    <td className="align-middle ps-3">{s.name || s._id}</td>
                                                                    <td className="align-middle text-end pe-3">
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-link text-danger p-0"
                                                                            onClick={() => onRemoveSound(s)}
                                                                            aria-label="Remove sound"
                                                                        >
                                                                            <Trash size={14} />
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            );
                                        })()}
                                    </>
                                )}
                                {canManageGallery && showManagementSections && (
                                    <div className="border rounded p-3 mt-2">
                                        <label className="form-label mb-2">Europeana ambient sounds</label>
                                        <div className="d-flex gap-2 mb-2">
                                            <input
                                                type="text"
                                                className="form-control form-control-lg"
                                                placeholder="Search keywords…"
                                                value={europeanaQuery}
                                                onChange={(e) => setEuropeanaQuery(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        void runEuropeanaSearch(1);
                                                    }
                                                }}
                                                disabled={europeanaLoading}
                                            />
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary py-2 text-nowrap"
                                                onClick={() => void runEuropeanaSearch(1)}
                                                disabled={europeanaLoading || !europeanaQuery.trim()}
                                            >
                                                {europeanaLoading ? (
                                                    <>
                                                        <Search size={16} className="me-1" />
                                                        Searching...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Search size={16} className="me-1" />
                                                        Search
                                                    </>
                                                )}
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary py-2 text-nowrap"
                                                onClick={onClearEuropeanaSearch}
                                                disabled={
                                                    europeanaLoading ||
                                                    (!europeanaQuery.trim() && !europeanaHasSearched)
                                                }
                                                aria-label="Clear search"
                                            >
                                                <XLg size={16} className="me-1" />
                                                Clear
                                            </button>
                                        </div>
                                        {europeanaHasSearched &&
                                            europeanaResults.length === 0 &&
                                            !europeanaLoading && (
                                                <div className="text-light small mb-2">
                                                    No sound results. Try different keywords.
                                                </div>
                                            )}
                                        {!europeanaLoading && europeanaHasSearched && (
                                        <div className="rounded overflow-hidden border" style={{ background: "#2a2a2a" }}>
                                            <div
                                                className={listStyles["objects-grid"]}
                                                style={{ maxHeight: "360px", overflowY: "auto", padding: "12px" }}
                                            >
                                                {europeanaResults.map((externalSound) => {
                                                    const streamUrl = europeanaHttpSoundPlaybackUrl(externalSound.url);
                                                    return (
                                                        <div
                                                            key={externalSound.sourceId}
                                                            className={listStyles["object-card"]}
                                                            style={{ cursor: "default" }}
                                                        >
                                                            <div className={listStyles["object-info"]}>
                                                                <h3 title={externalSound.name}>{externalSound.name || "Untitled"}</h3>
                                                                {streamUrl ? (
                                                                    <audio
                                                                        controls
                                                                        className="w-100 mt-1"
                                                                        style={{ height: 34, maxHeight: 40 }}
                                                                        preload="metadata"
                                                                        ref={(el) => {
                                                                            const id = externalSound.sourceId;
                                                                            if (el) europeanaAudioRefs.current.set(id, el);
                                                                            else europeanaAudioRefs.current.delete(id);
                                                                        }}
                                                                        onPlay={() => {
                                                                            pauseOtherEuropeanaPreviews(externalSound.sourceId);
                                                                            for (const a of audios) {
                                                                                a.pause();
                                                                                a.currentTime = 0;
                                                                            }
                                                                            onPauseBackgroundSound?.();
                                                                        }}
                                                                        onPause={scheduleResumeBackgroundIfNoPreviewsPlaying}
                                                                        onEnded={
                                                                            scheduleResumeBackgroundIfNoPreviewsPlaying
                                                                        }
                                                                    >
                                                                        <source src={streamUrl} />
                                                                    </audio>
                                                                ) : (
                                                                    <p className="small mb-0">No playable URL</p>
                                                                )}
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-outline-secondary mt-2 w-100"
                                                                    disabled={
                                                                        !!europeanaImportingId ||
                                                                        soundChangeInFlight ||
                                                                        !streamUrl ||
                                                                        muteInFlight
                                                                    }
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        void onEuropeanaUseAsBackground(externalSound);
                                                                    }}
                                                                >
                                                                    {(europeanaImportingId === externalSound.sourceId && soundChangeInFlight)
                                                                        ? "Adding…"
                                                                        : "Use as background sound"}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            {europeanaTotalResults > 0 && (
                                                <div
                                                    className="d-flex flex-wrap align-items-center gap-2 px-3 py-2 small border-top"
                                                    style={{ background: "#252525", color: "#a0a0a0" }}
                                                >
                                                    <label className="mb-0 d-flex align-items-center gap-1">
                                                        Per page
                                                        <select
                                                            className="form-select form-select-sm"
                                                            style={{ width: "auto", minWidth: 70 }}
                                                            value={europeanaPageSize}
                                                            onChange={(e) => {
                                                                const n = Number(e.target.value);
                                                                void runEuropeanaSearch(1, n);
                                                            }}
                                                            disabled={europeanaLoading}
                                                        >
                                                            <option value={12}>12</option>
                                                            <option value={24}>24</option>
                                                            <option value={48}>48</option>
                                                        </select>
                                                    </label>
                                                    <span className="ms-auto">
                                                        Page {europeanaPage} of {europeanaTotalPages} ({europeanaTotalResults}{" "}
                                                        total)
                                                    </span>
                                                    <div className="btn-group btn-group-sm">
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-secondary"
                                                            disabled={europeanaPage <= 1 || europeanaLoading}
                                                            aria-label="Previous page"
                                                            onClick={() => void runEuropeanaSearch(europeanaPage - 1)}
                                                        >
                                                            <ChevronLeft size={16} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-secondary"
                                                            disabled={
                                                                europeanaPage >= europeanaTotalPages || europeanaLoading
                                                            }
                                                            aria-label="Next page"
                                                            onClick={() => void runEuropeanaSearch(europeanaPage + 1)}
                                                        >
                                                            <ChevronRight size={16} />
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="border-top my-4" />

                    <div id="collaborators_section" className="mb-2">
                        <h6 className="fw-semibold mb-3 text-success d-flex align-items-center">
                            <People className="me-2" /> Collaborators
                        </h6>
                        {showManagementSections && (
                            <>
                                {canManageCollaborators && (
                                    <div className="mb-3">
                                        <label className="form-label">Add new collaborator</label>
                                        <div className="d-flex gap-2 mb-2">
                                        <input
                                            type="email"
                                            className="form-control form-control-lg"
                                            placeholder="Email address"
                                            value={newCollaboratorEmail}
                                            onChange={(e) => setNewCollaboratorEmail(e.target.value)}
                                        />
                                        <select
                                            className="form-select form-select-lg"
                                            style={{ minWidth: "140px" }}
                                            value={newCollaboratorRole}
                                            onChange={(e) => setNewCollaboratorRole(e.target.value as "editor" | "contributor")}
                                        >
                                            <option value="contributor">Contributor</option>
                                            <option value="editor">Editor</option>
                                        </select>
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary py-2"
                                            onClick={onAddCollaborator}
                                            disabled={!newCollaboratorEmail.trim() || addCollaboratorInFlight}
                                        >
                                            Add
                                        </button>
                                        </div>
                                    </div>
                                )}
                                <label className="form-label">Collaborators</label>
                                <div className="table-responsive rounded-3 overflow-hidden border shadow-sm collaborators-table">
                                    <table className="table table-sm table-hover mb-0">
                                        <thead>
                                            <tr>
                                                <th className="ps-3">Email</th>
                                                <th>Role</th>
                                                <th className="text-end pe-3" style={{ width: "100px" }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {collaboratorsDisplayList.length === 0 ? (
                                                <tr>
                                                    <td colSpan={3} className="text-muted small text-center py-4">
                                                        No collaborators yet.
                                                    </td>
                                                </tr>
                                            ) : collaboratorsDisplayList.map((c) => (
                                                    <tr key={c.userId}>
                                                        <td className="align-middle ps-3">{c.email}</td>
                                                        <td className="align-middle py-2">
                                                            {c.role === "owner" ? (
                                                                <span className="d-inline-flex align-items-center">
                                                                    <Lock className="me-1" size={14} /> Owner
                                                                </span>
                                                            ) : editingCollaboratorId === c.userId ? (
                                                                <div className="d-flex align-items-center gap-1">
                                                                    <select
                                                                        className="form-select form-select-sm"
                                                                        style={{ width: "auto" }}
                                                                        value={editRoleValue}
                                                                        onChange={(e) => setEditRoleValue(e.target.value as "editor" | "contributor")}
                                                                    >
                                                                        <option value="contributor">Contributor</option>
                                                                        <option value="editor">Editor</option>
                                                                    </select>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-outline-primary"
                                                                        onClick={() => onUpdateCollaboratorRole(c.userId, editRoleValue)}
                                                                    >
                                                                        Save
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-outline-secondary"
                                                                        onClick={() => setEditingCollaboratorId(null)}
                                                                    >
                                                                        Cancel
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                <span className="text-capitalize">{c.role}</span>
                                                            )}
                                                        </td>
                                                        <td className="align-middle text-end pe-3">
                                                            {c.role === "owner" ? (
                                                                null
                                                            ) : canManageCollaborators ? (
                                                                <div className="d-flex justify-content-end gap-1">
                                                                    <OverlayTrigger
                                                                        placement="top"
                                                                        overlay={<Tooltip>Edit role</Tooltip>}
                                                                    >
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-outline-secondary p-1"
                                                                            onClick={() => {
                                                                                setEditingCollaboratorId(c.userId);
                                                                                setEditRoleValue(c.role as "editor" | "contributor");
                                                                            }}
                                                                        >
                                                                            <Pencil size={14} />
                                                                        </button>
                                                                    </OverlayTrigger>
                                                                    <OverlayTrigger
                                                                        placement="top"
                                                                        overlay={<Tooltip>Remove collaborator</Tooltip>}
                                                                    >
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-outline-danger p-1"
                                                                            onClick={() => setConfirmRemoveCollaborator({ userId: c.userId, email: c.email })}
                                                                        >
                                                                            <Trash size={14} />
                                                                        </button>
                                                                    </OverlayTrigger>
                                                                </div>
                                                            ) : null}
                                                        </td>
                                                    </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer id="modal_footer">
                    <button type="button" className="btn btn-secondary px-4 py-2" onClick={onHide}>
                        Exit
                    </button>
                </Modal.Footer>
            </Modal>
            <ConfirmModal
                show={!!confirmRemoveCollaborator}
                onHide={() => setConfirmRemoveCollaborator(null)}
                onConfirm={async () => {
                    if (confirmRemoveCollaborator) {
                        await onRemoveCollaborator(confirmRemoveCollaborator.userId);
                        setConfirmRemoveCollaborator(null);
                    }
                }}
                title="Remove collaborator"
                message={confirmRemoveCollaborator ? `Are you sure you want to remove ${confirmRemoveCollaborator.email} from this gallery?` : ""}
                confirmText="Remove"
                cancelText="Cancel"
            />
            <ConfirmModal
                show={!!confirmRemoveSound}
                onHide={() => setConfirmRemoveSound(null)}
                onConfirm={onConfirmRemoveSound}
                title="Remove sound"
                message={confirmRemoveSound ? `Are you sure you want to remove "${confirmRemoveSound.name || confirmRemoveSound._id}" from this gallery?` : ""}
                confirmText="Remove"
                cancelText="Cancel"
            />
            <ToastContainer />
        </>
    )
}
